import { BlockLink } from 'BlockLink';
import { Card } from 'Card';
import clsx from 'clsx';
import { memo, useCallback, useMemo } from 'react';
import { Text } from 'Text';
import { useEventLogger } from 'FrontRoyalAngular';
import { type SidebarBoxKey } from 'AdmissionsGuidance';
import { type ButtonConfig } from '../StudentDashboardSidebar.types';
import { useMobileBoxContext } from '../MobileSidebar/MobileBoxContext';
import { useIsPrimaryMobileBox } from '../hooks/useIsPrimaryMobileBox';

type Props = {
    boxKey: SidebarBoxKey;
    headerText: string;
    subtitle?: string;
    children: React.ReactNode;
    buttons?: ButtonConfig[];
};

function BasicDesktopBoxComponent({ boxKey, headerText, subtitle, children, buttons }: Props) {
    const EventLogger = useEventLogger();
    const mobileBoxConfig = useMobileBoxContext();
    const isPrimaryMobileBox = useIsPrimaryMobileBox(boxKey);
    const handleButtonClick = useCallback(
        (button: ButtonConfig) => {
            EventLogger.log('student_dashboard_sidebar_box:clicked_button', {
                type: `${boxKey}:${button.id}`,
            });
            button.onPress?.();
        },
        [EventLogger, boxKey],
    );

    return (
        <BasicDesktopBoxPresentation
            boxKey={boxKey}
            headerText={headerText}
            subtitle={subtitle}
            buttons={buttons}
            handleButtonClick={handleButtonClick}
            isInMobileDrawer={!!mobileBoxConfig}
            isPrimaryMobileBox={isPrimaryMobileBox}
        >
            {children}
        </BasicDesktopBoxPresentation>
    );
}

export function BasicDesktopBoxPresentation({
    headerText,
    subtitle,
    children,
    buttons,
    handleButtonClick,
    isInMobileDrawer,
    isPrimaryMobileBox,
}: Props & {
    handleButtonClick: (button: ButtonConfig) => void;
    isInMobileDrawer: boolean;
    isPrimaryMobileBox: boolean;
}) {
    const header = useMemo(
        () => (
            <div className="flex w-full flex-col items-center justify-center text-center">
                <Text as="h3" color="black" weight="normal" className="m-0 text-lg leading-[25px]">
                    {headerText}
                </Text>
                {subtitle ? (
                    <Text as="div" color="slate-grey" weight="semibold" className="mt-2 text-xs">
                        {subtitle}
                    </Text>
                ) : null}
            </div>
        ),
        [headerText, subtitle],
    );

    return (
        <Card
            width="full"
            shadow={false}
            padding="compact-header"
            radius={isInMobileDrawer ? 8 : 5}
            header={header}
            dividers={{ afterHeader: true }}
            wrapperBorder={isPrimaryMobileBox ? 'allButStart' : 'default'}
        >
            <Text
                variant="p"
                color="slate-grey"
                weight="normal"
                className={clsx('text-base', buttons?.length && 'mb-5')}
            >
                {children}
            </Text>
            {buttons?.map(button => (
                <BlockLink
                    key={button.id}
                    onPress={() => handleButtonClick(button)}
                    anchorIcon="right-arrow"
                    className={clsx('text-start', button.className)}
                >
                    {button.text}
                </BlockLink>
            ))}
        </Card>
    );
}

export const BasicDesktopBox = memo(BasicDesktopBoxComponent) as typeof BasicDesktopBoxComponent;
